/*
 * The message list: conversations newest first under Today / Yesterday / This week / Earlier.
 *  - The header says where you are and how much is there; select anything and it becomes a toolbar for the
 *    selection (Archive, Delete, Spam, Move, Read, Unread, Star), where your eye already is.
 *  - Quick filters under it (Unread, Starred, Attachments), as the panel's filter bar does it: square toggles,
 *    the brand's pale tint when on. They narrow the folder through search, so they cover all of it, not a page.
 *  - Rows: a neutral initial, the sender and subject in the foreground weight when unread with a small brand dot,
 *    the snippet quiet. On a pointer, hovering a row offers Archive, Delete, Read and Star in place of its date.
 *    Shift-click selects a range; rows drag onto a folder to move there; on a phone, swipe left to archive and
 *    right to mark read.
 */
import { Fragment, useEffect, useRef, useState, type DragEvent, type MouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { cx } from "../lib/cx";
import { IconArrowLeft, IconArrowRight, IconX, Spinner } from "../ui/icons";
import { Menu, MenuContent, MenuTrigger } from "../ui/Menu";
import { Tooltip } from "../ui/Tooltip";
import { DRAG_TYPE } from "./FolderPane";
import { dateGroup, displayName, fullDate, listDate, plural } from "./format";
import { IconAlarm, IconArchiveBox, IconBell, IconBellOff, IconFolderMove, IconMail, IconMailOpen, IconPaperclip, IconSpam, IconStar, IconStarFilled, IconTag, IconTrashCan } from "./icons";
import { LabelItems, LaterItems, type LaterChoice } from "./later";
import { SenderAvatar } from "./parts";
import type { Label, Row } from "./types";

/** A row's key: its mailbox (in the unified inbox), folder and uid. */
export const rowKey = (r: Pick<Row, "folder" | "uid" | "mailboxId">) => `${r.mailboxId ?? ""}\u0000${r.folder}\u0000${r.uid}`;

export type QuickFilter = "unread" | "starred" | "attachments";
export type RowAction = "archive" | "trash" | "read" | "unread" | "star" | "mute" | "unmute";

type Props = {
  title: string;
  subtitle: string;
  rows: Row[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** What to show when there's nothing: a teaching empty state. */
  empty: ReactNode;
  /** Said while loading with nothing on screen yet. */
  loadingText: string | null;
  openKey: string | null;
  cursor: number;
  selected: Map<string, Row>;
  showFolder: boolean;
  folderName: (path: string) => string;
  /** Sent and Drafts show who it went to. */
  showRecipients: boolean;
  compact: boolean;
  grouped: boolean;
  pager: { from: number; to: number; total: number; prev: (() => void) | null; next: (() => void) | null } | null;
  notice: string | null;
  /** Quick filters, when the view has them (a folder; not search or Starred). */
  filters: Record<QuickFilter, boolean> | null;
  onFilter: (f: QuickFilter) => void;
  onOpen: (r: Row, index: number) => void;
  /** `range`: shift was held, so everything from the last one toggled to this one. */
  onToggleSelect: (r: Row, index: number, range: boolean) => void;
  onSelectAll: (all: boolean) => void;
  onRowAction: (r: Row, a: RowAction) => void;
  onSwipe: (r: Row, dir: "archive" | "read") => void;
  /** A drag began on a row: what it carries (the selection when the row is in it). */
  onDragRows: (r: Row) => number;
  bulk: { archive: () => void; trash: () => void; read: () => void; unread: () => void; move: () => void; spam: () => void; star: () => void; clear: () => void; inTrash: boolean; inSpam: boolean; mute: () => void; allMuted: boolean };
  /** Beside the title when nothing is selected ("Save this search"). */
  headerAction?: ReactNode;
  /** A label id's name, for the chips (null for one this mailbox doesn't have). */
  labelName: (id: string) => string | null;
  /** In the unified inbox: whose row it is, as a small neutral initial. */
  mailboxOf?: (r: Row) => { initial: string; address: string } | null;
  /** Snooze: a row's own (hover) and the selection's. */
  snooze: { choices: LaterChoice[]; onPick: (rows: Row[] | null, iso: string) => void; onCustom: (rows: Row[] | null) => void } | null;
  /** Labels for the selection. */
  labels: { list: Label[]; has: (id: string) => "all" | "some" | "none"; onToggle: (id: string, on: boolean) => void; onManage: () => void } | null;
};

const FILTERS: { id: QuickFilter; label: string }[] = [
  { id: "unread", label: "Unread" },
  { id: "starred", label: "Starred" },
  { id: "attachments", label: "Attachments" },
];

export function MessageList(p: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  const now = new Date();
  const allSelected = p.rows.length > 0 && p.rows.every((r) => p.selected.has(rowKey(r)));
  const some = p.selected.size > 0;

  // Keep the keyboard cursor in view.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${p.cursor}"]`)?.scrollIntoView({ block: "nearest" });
  }, [p.cursor]);

  let lastGroup = "";
  return (
    <>
      {/* The header, or with a selection, the toolbar for it. */}
      <div className={cx("flex h-12 shrink-0 items-center gap-2 border-b border-line px-3", some && "bg-selected")}>
        {p.rows.length ? (
          <input
            type="checkbox"
            className="h-4 w-4 shrink-0 accent-brand-600"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = some && !allSelected;
            }}
            onChange={(e) => p.onSelectAll(e.target.checked)}
            aria-label={allSelected ? "Clear the selection" : "Select every conversation on this page"}
          />
        ) : null}
        {some ? (
          <div className="flex min-w-0 flex-1 items-center gap-0.5" role="toolbar" aria-label="Selected conversations">
            <span className="mr-1.5 shrink-0 text-[13px] font-semibold text-selected-fg tabular-nums">{p.selected.size} selected</span>
            <BarButton onClick={p.bulk.archive} label="Archive (e)">
              <IconArchiveBox size={16} />
            </BarButton>
            <BarButton onClick={p.bulk.trash} label={p.bulk.inTrash ? "Delete forever (#)" : "Delete (#)"} danger>
              <IconTrashCan size={16} />
            </BarButton>
            <BarButton onClick={p.bulk.spam} label={p.bulk.inSpam ? "Not spam (!)" : "Spam (!)"}>
              <IconSpam size={16} />
            </BarButton>
            <BarButton onClick={p.bulk.move} label="Move to a folder (v)">
              <IconFolderMove size={16} />
            </BarButton>
            <BarButton onClick={p.bulk.read} label="Mark read (Shift+I)">
              <IconMailOpen size={16} />
            </BarButton>
            <BarButton onClick={p.bulk.unread} label="Mark unread (Shift+U)">
              <IconMail size={16} />
            </BarButton>
            <BarButton onClick={p.bulk.star} label="Star (s)">
              <IconStar size={16} />
            </BarButton>
            <BarButton onClick={p.bulk.mute} label={p.bulk.allMuted ? "Unmute" : "Mute conversation"}>
              {p.bulk.allMuted ? <IconBell size={16} /> : <IconBellOff size={16} />}
            </BarButton>
            {p.snooze ? (
              <BarMenu label="Snooze">
                <IconAlarm size={16} />
                <LaterItems choices={p.snooze.choices} onPick={(iso) => p.snooze!.onPick(null, iso)} onCustom={() => p.snooze!.onCustom(null)} />
              </BarMenu>
            ) : null}
            {p.labels ? (
              <BarMenu label="Label">
                <IconTag size={16} />
                <LabelItems labels={p.labels.list} has={p.labels.has} onToggle={p.labels.onToggle} onManage={p.labels.onManage} />
              </BarMenu>
            ) : null}
            <BarButton onClick={p.bulk.clear} label="Clear the selection (Esc)" className="ml-auto">
              <IconX size={15} />
            </BarButton>
          </div>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="flex min-w-0 flex-1 items-baseline gap-2">
              <h2 className="truncate text-[15px] font-semibold text-fg">{p.title}</h2>
              <span className="truncate text-[12px] text-dim">{p.subtitle}</span>
            </div>
            {p.headerAction}
          </div>
        )}
      </div>

      {p.filters ? (
        <div className="flex shrink-0 items-center gap-1.5 border-b border-line-soft px-3 py-2" role="group" aria-label="Show only">
          {FILTERS.map((f) => {
            const on = p.filters![f.id];
            return (
              <button
                key={f.id}
                type="button"
                aria-pressed={on}
                onClick={() => p.onFilter(f.id)}
                className={cx(
                  "inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-control)] px-2.5 text-[12.5px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-brand-500",
                  on ? "bg-selected text-selected-fg shadow-[inset_0_0_0_1px_var(--color-brand-500)]" : "bg-surface text-body shadow-[inset_0_0_0_1px_var(--hx-line-strong)] hover:text-fg hover:shadow-[inset_0_0_0_1px_var(--hx-line-hover)]",
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      ) : null}

      {p.loading ? (
        <div className="relative h-0.5 shrink-0 overflow-hidden bg-transparent" role="status" aria-label="Loading messages">
          <div className="absolute inset-y-0 left-0 w-1/3 animate-pulse bg-info-fill" />
        </div>
      ) : null}
      {p.notice ? <div className="shrink-0 border-b border-line-soft bg-panel/60 px-3 py-1.5 text-[12px] text-dim">{p.notice}</div> : null}

      <div ref={listRef} className={cx("min-h-0 flex-1 overflow-y-auto", p.loading && p.rows.length > 0 && "opacity-60")}>
        {p.error ? (
          <div className="px-6 py-10 text-center">
            <p className="text-[14px] font-medium text-fg">This list didn't load</p>
            <p className="mt-1 text-[13px] text-dim">{p.error}</p>
            <button type="button" className="mt-3 text-[13px] font-medium text-link hover:underline" onClick={p.onRetry}>
              Try again
            </button>
          </div>
        ) : !p.loading && !p.rows.length ? (
          p.empty
        ) : p.loading && !p.rows.length ? (
          p.loadingText ? (
            <div className="flex flex-col items-center gap-3 px-6 py-12 text-center text-[13px] text-dim" role="status">
              <Spinner size={18} />
              {p.loadingText}
            </div>
          ) : (
            <div className="space-y-px" aria-hidden="true">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-start gap-3 px-4 py-3">
                  <div className="hx-skeleton h-8 w-8 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="hx-skeleton h-3 w-1/3 rounded" />
                    <div className="hx-skeleton h-3 w-2/3 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )
        ) : null}
        {p.rows.map((r, i) => {
          const group = p.grouped ? dateGroup(r.date, now) : "";
          const heading = group && group !== lastGroup ? group : null;
          lastGroup = group || lastGroup;
          return (
            <Fragment key={rowKey(r)}>
              {heading ? <div className="px-4 pt-3 pb-1 text-[12px] font-medium text-dim select-none">{heading}</div> : null}
              <MessageRow
                row={r}
                index={i}
                active={p.openKey === rowKey(r)}
                cursor={p.cursor === i}
                selected={p.selected.has(rowKey(r))}
                compact={p.compact}
                showFolder={p.showFolder}
                folderName={p.folderName}
                showRecipients={p.showRecipients}
                now={now}
                onOpen={() => p.onOpen(r, i)}
                onToggle={(range) => p.onToggleSelect(r, i, range)}
                onAction={(a) => p.onRowAction(r, a)}
                onSwipe={(dir) => p.onSwipe(r, dir)}
                onDragRows={() => p.onDragRows(r)}
                labelName={p.labelName}
                mailbox={p.mailboxOf?.(r) ?? null}
                snooze={p.snooze ? { choices: p.snooze.choices, onPick: (iso) => p.snooze!.onPick([r], iso), onCustom: () => p.snooze!.onCustom([r]) } : null}
              />
            </Fragment>
          );
        })}
      </div>

      {p.pager ? (
        <div className="flex h-10 shrink-0 items-center justify-between border-t border-line px-3 text-[12px] text-dim">
          <span className="tabular-nums">
            {p.pager.from.toLocaleString("en-US")}–{p.pager.to.toLocaleString("en-US")} of {p.pager.total.toLocaleString("en-US")}
          </span>
          <div className="flex items-center gap-1">
            <button type="button" disabled={!p.pager.prev} onClick={p.pager.prev ?? undefined} className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40" aria-label="Newer conversations">
              <IconArrowLeft size={14} />
            </button>
            <button type="button" disabled={!p.pager.next} onClick={p.pager.next ?? undefined} className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40" aria-label="Older conversations">
              <IconArrowRight size={14} />
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function BarButton({ onClick, label, children, danger, className }: { onClick: () => void; label: string; children: ReactNode; danger?: boolean; className?: string }) {
  return (
    <Tooltip content={label}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className={cx("inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-body transition-colors", danger ? "hover:bg-bad-bg hover:text-bad" : "hover:bg-surface hover:text-fg", className)}
      >
        {children}
      </button>
    </Tooltip>
  );
}

/** A toolbar button that opens a menu (Snooze, Label): the first child is its icon, the rest the menu. */
function BarMenu({ label, children }: { label: string; children: [ReactNode, ReactNode] }) {
  return (
    <Menu>
      <Tooltip content={label}>
        <MenuTrigger aria-label={label} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-body transition-colors hover:bg-surface hover:text-fg data-[state=open]:bg-surface">
          {children[0]}
        </MenuTrigger>
      </Tooltip>
      <MenuContent align="start" className="w-64">
        {children[1]}
      </MenuContent>
    </Menu>
  );
}

/** One action in a row's hover set: a small icon button that doesn't open the row. */
function RowButton({ label, onClick, children, danger }: { label: string; onClick: () => void; children: ReactNode; danger?: boolean }) {
  return (
    <Tooltip content={label}>
      <button
        type="button"
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className={cx("inline-flex h-6 w-6 items-center justify-center rounded-[5px] text-dim transition-colors", danger ? "hover:bg-bad-bg hover:text-bad" : "hover:bg-panel hover:text-fg")}
      >
        {children}
      </button>
    </Tooltip>
  );
}

function MessageRow({
  row: r,
  index,
  active,
  cursor,
  selected,
  compact,
  showFolder,
  folderName,
  showRecipients,
  now,
  onOpen,
  onToggle,
  onAction,
  onSwipe,
  onDragRows,
  labelName,
  mailbox,
  snooze,
}: {
  row: Row;
  index: number;
  active: boolean;
  cursor: boolean;
  selected: boolean;
  compact: boolean;
  showFolder: boolean;
  folderName: (path: string) => string;
  showRecipients: boolean;
  now: Date;
  onOpen: () => void;
  onToggle: (range: boolean) => void;
  onAction: (a: RowAction) => void;
  onSwipe: (dir: "archive" | "read") => void;
  onDragRows: () => number;
  labelName: (id: string) => string | null;
  mailbox: { initial: string; address: string } | null;
  snooze: { choices: LaterChoice[]; onPick: (iso: string) => void; onCustom: () => void } | null;
}) {
  const [dx, setDx] = useState(0);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const chips = (r.labels ?? []).map((id) => labelName(id)).filter((x): x is string => !!x);
  const start = useRef<{ x: number; y: number; id: number; swiping: boolean } | null>(null);
  const who = showRecipients && r.to.length ? r.to : r.from ? [r.from] : [];
  const name = showRecipients && r.to.length ? `To: ${r.to.map((x) => displayName(x)).join(", ")}` : r.draft ? "Draft" : displayName(r.from);
  const seed = who[0]?.address ?? "?";

  // Touch swipes: left archives, right marks read (or unread).
  const onPointerDown = (e: ReactPointerEvent) => {
    if (e.pointerType !== "touch") return;
    start.current = { x: e.clientX, y: e.clientY, id: e.pointerId, swiping: false };
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    const s = start.current;
    if (!s || s.id !== e.pointerId) return;
    const mx = e.clientX - s.x;
    if (!s.swiping && Math.abs(mx) > 12 && Math.abs(mx) > Math.abs(e.clientY - s.y) * 1.5) s.swiping = true;
    if (s.swiping) setDx(Math.max(-160, Math.min(160, mx)));
  };
  const onPointerEnd = () => {
    const s = start.current;
    start.current = null;
    if (s?.swiping) {
      if (dx < -90) onSwipe("archive");
      else if (dx > 90) onSwipe("read");
    }
    setDx(0);
  };
  const onDragStart = (e: DragEvent) => {
    const n = onDragRows();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(DRAG_TYPE, String(n));
    e.dataTransfer.setData("text/plain", n === 1 ? r.subject || "(no subject)" : `${n} conversations`);
  };
  const toggle = (e: MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onToggle(e.shiftKey);
  };

  return (
    <div className="relative overflow-hidden" data-index={index}>
      {dx !== 0 ? (
        <div className={cx("absolute inset-0 flex items-center px-5", dx < 0 ? "justify-end bg-brand-solid text-on-brand" : "justify-start bg-[var(--hx-btn-dark)] text-[var(--hx-btn-dark-fg)]")}>
          {dx < 0 ? (
            <span className="inline-flex items-center gap-1.5 text-[13px] font-medium">
              <IconArchiveBox size={16} />
              Archive
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[13px] font-medium">
              <IconMailOpen size={16} />
              {r.unread ? "Mark read" : "Mark unread"}
            </span>
          )}
        </div>
      ) : null}
      <div
        role="button"
        tabIndex={0}
        draggable
        onDragStart={onDragStart}
        aria-current={active ? "true" : undefined}
        aria-label={`${r.unread ? "Unread. " : ""}${name}: ${r.subject || "(no subject)"}`}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target === e.currentTarget) onOpen();
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        style={dx ? { transform: `translateX(${dx}px)` } : undefined}
        className={cx(
          "group/row relative cursor-pointer border-b border-line-soft px-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset",
          compact ? "py-2" : "py-3",
          active ? "bg-selected" : selected ? "bg-panel" : dx ? "bg-surface" : "bg-surface hover:bg-hover",
          cursor && !active && "ring-1 ring-line-hover ring-inset",
          dx === 0 && "touch-pan-y",
        )}
      >
        {r.unread ? <span aria-hidden="true" className={cx("absolute left-1.5 h-1.5 w-1.5 rounded-full bg-brand-500", compact ? "top-[15px]" : "top-[19px]")} /> : null}
        <div className="flex items-start gap-3 pl-2">
          <input
            type="checkbox"
            className={cx("h-4 w-4 shrink-0 accent-brand-600", compact ? "mt-0.5" : "mt-2")}
            checked={selected}
            onChange={() => {}}
            onClick={toggle}
            aria-label={`Select ${name}: ${r.subject || "(no subject)"}`}
          />
          {compact ? null : <SenderAvatar address={seed} name={who[0]?.name ?? null} />}
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className={cx("truncate text-[14px]", r.unread ? "font-semibold text-fg" : "text-body", r.draft && !showRecipients && "text-bad")}>{name}</span>
              {r.count > 1 ? <span className="shrink-0 text-[12px] text-dim tabular-nums">{r.count}</span> : null}
              <span className="ml-auto flex shrink-0 items-center gap-1.5">
                {r.muted ? <IconBellOff size={13} className="text-dim" title="Muted" /> : null}
                {r.hasAttachment ? <IconPaperclip size={13} className="text-dim" title="Has attachments" /> : null}
                {r.starred ? <IconStarFilled size={13} className="text-brand-500" title="Starred" /> : null}
                <Tooltip content={fullDate(r.date)}>
                  <span className={cx("text-[12px] tabular-nums [@media(hover:hover)]:group-hover/row:invisible", r.unread ? "font-medium text-fg" : "text-dim")}>{listDate(r.date, now)}</span>
                </Tooltip>
              </span>
            </div>
            <div className="flex min-w-0 items-center gap-1.5">
              {mailbox ? (
                <Tooltip content={mailbox.address}>
                  <span aria-label={`In ${mailbox.address}`} className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-[4px] bg-panel px-1 text-[10.5px] font-semibold text-dim uppercase shadow-[inset_0_0_0_1px_var(--hx-line)]">
                    {mailbox.initial}
                  </span>
                </Tooltip>
              ) : null}
              <span className={cx("min-w-0 truncate text-[13.5px]", r.unread ? "font-medium text-fg" : "text-body")}>{r.subject || "(no subject)"}</span>
            </div>
            {!compact && r.snippet ? <p className="mt-0.5 truncate text-[12.5px] text-dim">{r.snippet}</p> : null}
            {showFolder || chips.length ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {showFolder ? <span className="inline-block rounded-[5px] bg-panel px-1.5 py-px text-[11.5px] text-dim">{folderName(r.folder)}</span> : null}
                {chips.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1 rounded-[5px] px-1.5 py-px text-[11.5px] text-body shadow-[inset_0_0_0_1px_var(--hx-line-strong)]">
                    <IconTag size={10} className="text-dim" />
                    {c}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        {/* Hovering offers the common actions in the date's place (pointers only; phones swipe). Kept open while its snooze menu is. */}
        <div className={cx("absolute right-2 items-center gap-px rounded-md bg-surface p-px shadow-[0_0_0_1px_var(--hx-line),0_4px_12px_-6px_rgba(15,23,42,0.18)] [@media(hover:hover)]:group-hover/row:flex", snoozeOpen ? "flex" : "hidden", compact ? "top-1" : "top-2")}>
          <RowButton label="Archive (e)" onClick={() => onAction("archive")}>
            <IconArchiveBox size={14} />
          </RowButton>
          <RowButton label="Delete (#)" onClick={() => onAction("trash")} danger>
            <IconTrashCan size={14} />
          </RowButton>
          <RowButton label={r.unread ? "Mark read" : "Mark unread"} onClick={() => onAction(r.unread ? "read" : "unread")}>
            {r.unread ? <IconMailOpen size={14} /> : <IconMail size={14} />}
          </RowButton>
          <RowButton label={r.starred ? "Unstar (s)" : "Star (s)"} onClick={() => onAction("star")}>
            {r.starred ? <IconStarFilled size={14} className="text-brand-500" /> : <IconStar size={14} />}
          </RowButton>
          <RowButton label={r.muted ? "Unmute" : "Mute conversation"} onClick={() => onAction(r.muted ? "unmute" : "mute")}>
            {r.muted ? <IconBell size={14} /> : <IconBellOff size={14} />}
          </RowButton>
          {snooze ? (
            <span onClick={(e) => e.stopPropagation()}>
              <Menu open={snoozeOpen} onOpenChange={setSnoozeOpen}>
                <Tooltip content="Snooze">
                  <MenuTrigger aria-label="Snooze" className="inline-flex h-6 w-6 items-center justify-center rounded-[5px] text-dim transition-colors hover:bg-panel hover:text-fg">
                    <IconAlarm size={14} />
                  </MenuTrigger>
                </Tooltip>
                <MenuContent align="end" className="w-64">
                  <LaterItems choices={snooze.choices} onPick={snooze.onPick} onCustom={snooze.onCustom} />
                </MenuContent>
              </Menu>
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function listSubtitle(kind: "folder" | "starred" | "search" | "unified", d: { messages?: number; unseen?: number; total?: number; truncated?: boolean } | undefined): string {
  if (!d) return "";
  if (kind === "unified") return `${plural(d.total ?? 0, "conversation")}${d.unseen ? ` · ${d.unseen.toLocaleString("en-US")} unread` : ""}`;
  if (kind === "folder") return `${plural(d.messages ?? 0, "message")}${d.unseen ? ` · ${d.unseen.toLocaleString("en-US")} unread` : ""}`;
  if (kind === "starred") return plural(d.total ?? 0, "starred message");
  return `${plural(d.total ?? 0, "result")}${d.truncated ? ", newest 100 shown" : ""}`;
}
